/*
 * VNCcommander - The brilliant centerpiece of VNClagoon with your activity stream and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { getAllContacts, getContactById, getNetworkInformation, getUserProfile, RootState } from "../reducers";
import { Store } from "@ngrx/store";
import { ConfigService } from "../config.service";
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpParams, HttpRequest } from "@angular/common/http";
import { fromEvent, merge, Observable, of, Subject, throwError } from "rxjs";
import { SearchItem, SearchResponse } from "../common/models/mail-models/search-item";
import { catchError, map, mapTo, take, tap } from "rxjs/operators";
import { CommonUtils } from "../common/utils/common-util";
import { OnlineStatus, SetCountForApps, SetUnreadCountForApps, UpdateNetworkInformation } from "../actions/app";
import { CommanderConstants } from "../common/utils/commander.constants";
import * as moment from "moment";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Message } from "../common/models/message.model";
import { AuthService } from "src/app/common/providers/auth.service";
import { SaveSendMessage } from "src/app/common/models/mail-models/save-send.model";
import { SearchRequest } from "src/app/common/models/mail-models/search-request.model";
import { TaskUser } from "../common/models/task-models/task-user";
import { ContactInfo } from "src/app/common/models/contact-info.model";
import { FeedRequest } from "../common/models/feed-request.model";
import { User } from "@sentry/browser";
import { Conversation } from "../common/models/conversation.model";
import { XmppService } from "./xmpp.service";
import { DashboardResponse } from "../common/models/capacity.model";
import { TranslateService } from "@ngx-translate/core";
import { switchMap } from 'rxjs/operators';
import { ContactRest } from "../common/models/ldap-contact.model";

const VNCTALK_API = CommanderConstants.VNCTALK_API;
const VNCMAIL_API = CommanderConstants.VNCMAIL_API;
const VNCTASK_API = CommanderConstants.VNCTASK_API;

@Injectable()
export class AppService {
    private isCordovaOrElectron = environment.isCordova || environment.isElectron;
    isNetworkOnline: any;
    loggedInUser: User;
    mainFrameLoaded$ = new Subject();

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private domSanitizer: DomSanitizer,
        private auth: AuthService,
        private store: Store<RootState>,
        private xmppService: XmppService,
        private translate: TranslateService) {
            this.store.select(getNetworkInformation).subscribe(lastState => {
                if (!lastState) {
                  return;
                }

                const isConnected = lastState.onlineState;

                // if network changed
                if (this.isNetworkOnline !== isConnected) {
                  this.isNetworkOnline = isConnected;
                  this.store.dispatch(new OnlineStatus(isConnected));
                }

                // edge case
                if (!isConnected && navigator.onLine) {
                  this.store.dispatch(new UpdateNetworkInformation({
                    onlineState: navigator.onLine,
                    connectionType: (lastState && lastState.connectionType) || "unknown"
                  }));
                }
            });

            // network changes
            merge(
                fromEvent(window, "online").pipe(mapTo(true)),
                fromEvent(window, "offline").pipe(mapTo(false))
            ).subscribe(this.notifyConnectionStatus.bind(this));
    }

    private notifyConnectionStatus(isConnected: boolean) {
        this.store.dispatch(new OnlineStatus(isConnected));

        let connectionType = "unknown";
        if (navigator.connection) {
            connectionType = navigator.connection.type ? navigator.connection.type : navigator.connection.effectiveType || "unknown";
        }

        //
        this.store.dispatch(new UpdateNetworkInformation({
            onlineState: isConnected,
            connectionType: connectionType
        }));
    }

    public getConfig(serverUrl: string): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(serverUrl + "/api/config", { headers: headers });
    }

    public loadConfig(): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + "/api/config", { headers: headers });
    }

    public getDashboard(): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + "/api/vncfluid/dashboard", { headers: headers });
    }

    public getCapacities(): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + "/api/capacity", { headers: headers });
    }

    public getWorkspaces(params?: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + "/api/vncfluid/workspaces", { headers: headers, params });
    }

    public getMembers(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + `/api/vncfluid/workspaces/${id}/members`, { headers: headers });
    }

    public getEntities(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + `/api/vncfluid/workspaces/${id}/entities`, { headers: headers });
    }

    public createWorkspace(payload: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.post(this.getAPI() + "/api/vncfluid/workspaces", payload, { headers: headers });
    }

    public updateMembers(id: any, payload: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.post(this.getAPI() + `/api/vncfluid/workspaces/${id}/members`, payload, { headers: headers });
    }

    public updateEntities(id: any, payload: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.post(this.getAPI() + `/api/vncfluid/workspaces/${id}/entities`, payload, { headers: headers });
    }

    public archiveWorkspace(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.post(this.getAPI() + `/api/vncfluid/workspaces/${id}/archive`, null, { headers: headers });
    }

    public unarchiveWorkspace(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.post(this.getAPI() + `/api/vncfluid/workspaces/${id}/unarchive`, null, { headers: headers });
    }

    public deleteWorkspace(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.delete(this.getAPI() + `/api/vncfluid/workspaces/${id}`, { headers: headers });
    }

    public removeMembers(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.delete(this.getAPI() + `/api/vncfluid/workspaces/${id}/members`, { headers: headers });
    }

    public removeEntities(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.delete(this.getAPI() + `/api/vncfluid/workspaces/${id}/entities`, { headers: headers });
    }

    public updateWorkspace(payload: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.put(this.getAPI() + "/api/vncfluid/workspaces", payload, { headers: headers });
    }

    public getWorkspace(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + `/api/vncfluid/workspaces/${id}`,  {headers: headers });
    }

    public updateCapacities(payload: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.post(this.getAPI() + "/api/capacity", payload, { headers: headers });
    }

    public deleteCapacity(id: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.delete(this.getAPI() + `/api/capacity/${id}`, { headers: headers });
    }

    public getUnreadMessage(): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        return this.http.get(this.getAPI() + `/api/vnctalk/getTotalUnreadCount`, { headers: headers });
    }

    public getUnreadMessageFromConversation(): Observable<any> {
      let headers = new HttpHeaders({ "Content-Type": "application/json" });

      return this.http.get(this.getAPI() + `/api/vnctalk/convHistory?resultCount=200&offset=0&updated_after=0`, { headers: headers });
  }

    public getUnreadMail(): Observable<any> {
      let headers = new HttpHeaders({ "Content-Type": "application/json" });
      const payload = {
        "start": 0,
        "limit": 1,
        "attachmentType": "",
        "isUnread": true,
        "mailFlags": "",
        "orFilters": [],
        "sorts": { "score": "desc" },
        "searchText": ""
      };
      return this.http.post(this.getAPI() + "/api/vncmail/mailSearch", payload, { headers: headers });
    }

    public getTotalMeetings(): Observable<any> {
      let headers = new HttpHeaders({ "Content-Type": "application/json" });
      const beginDate = new Date();
      return this.getMailFolders("appointment").pipe(take(1), switchMap(v => {
        let query = "inid:\"10\"";
        if (v && v.folder && v.folder[0] && v.folder[0].folder) {
          query = v.folder[0].folder.map(f => `inid:"${f.id}"`).join(" OR ");
        }
        const payload = {
          calExpandInstStart: moment(beginDate).format("x"),
          calExpandInstEnd: moment(beginDate).add(7, "days").format("x"),
          limit: 100,
          offset: 0,
          query: query,
          types: "appointment"
        };
        return this.http.post(this.getAPI() + "/api/vncmail/searchRequest", payload, { headers: headers });
      }));
    }

    private getAPI(): string {
        return this.configService.API_URL;
    }

    public searchDocs(body: FeedRequest): Observable<SearchResponse> {
        let myContacts = [];
        let loggedInUser = null;
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        return this.http.post(this.getAPI() + "/api/search", body, { headers: headers }).pipe(
          tap(
            (res: any) => {
              this.http.get(this.getAPI() + VNCTALK_API + "/getTotalUnreadCount", { headers: headers}).subscribe(
                (data: any) => {
                  let unreadCount = {
                    talk: +data.unread,
                  };
                  if (res.facet_counts && res.facet_counts.facet_queries) {
                    const queries = res.facet_counts.facet_queries;
                    unreadCount["mail"] = +queries["(mail_unread_b:true AND mail_folder_id_i:2)"];
                    unreadCount["task"] = +queries["((type_s:issue AND issue_type:task) AND -status_s:Completed)"];
                    this.store.dispatch(new SetUnreadCountForApps(unreadCount));
                  }

                }
              );
            }
          ),
          tap(
            (res: any) => {
              this.store.select(getAllContacts).subscribe( contacts => {
                myContacts = contacts.map( v => ({email: v.defaultMail, name: v.name}));
              });
              this.store.select(getUserProfile).subscribe(user => {
                this.loggedInUser = user;
                loggedInUser = user;
              });
            }
          ),
          map((res: any) => {
            let docs = [];
            let numFound = 0;
            let start = 0;
            let groups = {};
            if (res.response) {
                docs = res.response.docs.map(val => {
                    return this.mapSearchItem(val);
                });
                numFound = res.response.numFound;
                start = res.response.start;
            } else if (!!res.grouped || !!res.ungrouped) {
              if (res.grouped && res.grouped.talk_jid_s && res.grouped.talk_jid_s.groups) {
                const talkDocs = [];
                res.grouped.talk_jid_s.groups.forEach(group => {
                  if (group.doclist.start) {
                    start = group.doclist.start;
                  }
                  if (!!group.groupValue && group.doclist.docs.length > 0 && group.groupValue !== loggedInUser.defaultMail) {
                    const doc = this.mapTalkGroupIntoSearchItem(group, myContacts);
                    talkDocs.push(doc);
                  }
                });
                docs = [...talkDocs];
                numFound = res.grouped.talk_jid_s.matches;
              }

              if (res.ungrouped && res.ungrouped.docs) {
                const nonTalkDocs = res.ungrouped.docs.map(val => {
                  return this.mapSearchItem(val);
                });
                const nonTalkNumFound = res.ungrouped.numFound;
                docs = [...docs, ...nonTalkDocs];
                numFound = numFound + nonTalkNumFound;
              }

              if (body.sort &&  body.sort.includes("created_dt asc")) {
                docs.sort((a, b) => (a.createdDt > b.createdDt) ? 1 : -1);
              } else {
                docs.sort((a, b) => (a.createdDt < b.createdDt) ? 1 : -1);
              }
            } else if (res.grouped && res.grouped.type_s && res.grouped.type_s.groups) {
                numFound = res.grouped.type_s.matches;
                res.grouped.type_s.groups.forEach(group => {
                    if (group.doclist.start) {
                        start = group.doclist.start;
                    }
                    const _docs = group.doclist.docs.map(val => {
                        return this.mapSearchItem(val);
                    });
                    groups[group.groupValue] = _docs;
                    docs = [...docs, ..._docs];
                });
            } else if (res.grouped && res.grouped.from_s && res.grouped.from_s.groups) {
                numFound = res.grouped.from_s.matches;
                res.grouped.from_s.groups.forEach(group => {
                    if (group.doclist.start) {
                        start = group.doclist.start;
                    }
                    const _docs = group.doclist.docs.map(val => {
                        return this.mapSearchItem(val);
                    });
                    groups[group.groupValue] = _docs;
                    docs = [...docs, ..._docs];
                });
            }
            if (res.facet_counts && res.facet_counts.facet_fields && res.facet_counts.facet_fields.type_s) {
                const apps = {};
                const type = res.facet_counts.facet_fields.type_s;
                for (let i = 0; i <= type.length; i++) {
                    if (typeof type[i] === "string" && i < type.length - 1) {
                        apps[type[i]] = type[i + 1];
                    }
                }

                this.store.dispatch(new SetCountForApps(apps));
            } else {
                this.store.dispatch(new SetCountForApps({}));
            }
            return { docs: docs, numFound: numFound, start: start, groups: groups } as SearchResponse;
        }));
    }


    public uploadAvatar(files): Observable<any> {
        let headers = new HttpHeaders();
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Authorization": token });
        }
        return this.http.post(this.getAPI() + "/api/upload_avatar", files,
            {
                headers: headers,
                responseType: "text"
            });
    }

    public getMyProducts(): Observable<any> {
        let headers = new HttpHeaders();
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Authorization": token });
        }
        if (localStorage.getItem("products")) {
          let products;
          try {
            products = JSON.parse(localStorage.getItem("products"));
            return of({products});
         } catch (e) {
            console.error(e);
            return of({products: []});
         }
        }
        return this.http.get(this.getAPI() + "/api/my-products", {
            headers: headers
        });
    }

    public getLoggedInUserInfo(): Observable<any> {
        let headers = new HttpHeaders();
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Authorization": token });
        }
        return this.http.get(this.getAPI() + "/api/getLoggedInUserInfo", { headers: headers }).pipe(map((res: any) => {
            if (!!res && res !== null && res.contact) {
              return this.getMapContacts(res.contact);
            } else {
              let email = "abc@sdasd.sd";
              const contact = new ContactInfo();
              contact.jid = email;
              contact.fullName = email.substring(0, email.indexOf("@"));
              contact.phones = [];
              contact.emails = [];
              contact.urls = [];
              return contact;
            }
        }));
    }

    public getMyProjects(): Observable<any> {
      if (localStorage.getItem("projects")) {
        let projects;
          try {
            projects = JSON.parse(localStorage.getItem("projects"));
            return of({projects});
         } catch (e) {
            console.error(e);
            return of({projects: []});
         }
      }
        return this.get("/api/my-projects", true);
    }

    public getMyProjectDetails(projectId: number): Observable<any> {
      return this.get("/api/my-projects/" + projectId, true);
    }

    public getPriorities(): Observable<any> {
      if (localStorage.getItem("issue_priority")) {
        let issue_priority;
          try {
            issue_priority = JSON.parse(localStorage.getItem("issue_priority"));
            if (new Date() > new Date(issue_priority.expiry)) {
              return this.get("/api/issue-priorities", true);
            } else {
              return of(issue_priority);
            }
         } catch (e) {
            console.error(e);
            return of({});
         }
      }
        return this.get("/api/issue-priorities", true);
    }

    public getStatuses(): Observable<any> {
      if (localStorage.getItem("issue_statuses")) {
        let issue_statuses;
        try {
          issue_statuses = JSON.parse(localStorage.getItem("issue_statuses"));
          return of({issue_statuses});
       } catch (e) {
          console.error(e);
          return of({issue_statuses: []});
       }

      }
        return this.get("/api/issue-statuses", true);
    }

    public getUploadUrl(fileName, fileSize): Observable<any> {
        let headers = new HttpHeaders();
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Authorization": token });
        }
        return this.http.get(this.getAPI() + "/api/get-upload-url",
            {
                headers: headers,
                params: {filename: fileName, fileSize: fileSize},
                responseType: "text"
            });
    }

    public searchLDAP(keyword): Observable<any> {
        let headers = new HttpHeaders();
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Authorization": token });
        }
        if (localStorage.getItem("search-ldap")) {
          let ldpData;
          try {
            ldpData = JSON.parse(localStorage.getItem("search-ldap"));
            return of(ldpData);
         } catch (e) {
            console.error(e);
            return of([]);
         }
        }
        return this.http.post<any[]>(this.getAPI() + VNCTALK_API + "/search-ldap", {
            keyword: keyword
        }).pipe(map(res => {
            if (res === null) {
              localStorage.setItem("search-ldap", JSON.stringify([]));
                return [];
            }
            let ldapData = res.map(user => {
                  return {
                      name: CommonUtils.getDisplayNameFromLDAPEntry(user),
                      email: user.mail[0],
                      ldapData: user
                  };
              });
              localStorage.setItem("search-ldap", JSON.stringify(ldapData));
            return ldapData;
        }));
    }

    getConvHistory(offset: number, limit: number = 100, updated_after?: number): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: any = {
            headers: headers,
            params: {
                "resultCount": limit,
                "offset": offset
            }
        };
        if (!!updated_after) {
            options.params.updated_after = Math.floor(updated_after / 1000);
            localStorage.setItem("lastPollingTimeStamp", String(new Date().getTime()));
        }
        return this.http.get(this.getAPI() + VNCTALK_API + "/convHistory", options);
    }

    private buildHeaders(useAuthHeaders: boolean, headers?: HttpHeaders): HttpHeaders {
        if (!useAuthHeaders) {
            return headers;
        }
        let mutatedHeaders: HttpHeaders;
        if (!!headers) {
            mutatedHeaders = headers;
        } else {
            mutatedHeaders = new HttpHeaders();
        }
        const token = localStorage.getItem("token");
        if (token && this.isCordovaOrElectron) {
            if (mutatedHeaders.has("Content-Type") && mutatedHeaders.get("Content-Type") === "text/plain") {
                return mutatedHeaders.set("Authorization", token).set("Content-Type", "text/plain").set("Accept", "text/plain");
            }
            return mutatedHeaders.set("Authorization", token).set("Content-Type", "application/json").set("Accept", "application/json");
        } else {
            if (mutatedHeaders.has("Content-Type") && mutatedHeaders.get("Content-Type") === "text/plain") {
                return mutatedHeaders.set("Content-Type", "text/plain").set("Accept", "text/plain");
            }
            return mutatedHeaders.set("Content-Type", "application/json").set("Accept", "application/json");
        }
    }

    private buildParams(data: any): HttpParams {
        let params = new HttpParams();
        if (data) {
          for (const key of Object.keys(data)) {
            params = params.append(key, data[key]);
          }
        }
        return params;
    }

    get<T>(url: string, useAuthHeaders: boolean, data?: any, headers?: HttpHeaders,
        useBaseUrl: boolean = true, responseType?: string): Observable<T> {
          const options = {
          params: this.buildParams(data),
          headers: this.buildHeaders(useAuthHeaders, headers)
        };
        if (responseType) {
          options["responseType"] = responseType;
        }
        let baseUrl = this.getAPI() + url;
        if (!useBaseUrl) {
          baseUrl = url;
        }
        return this.http.get<T>(baseUrl, options);
    }

    post<T>(url: string, useAuthHeaders: boolean, data: any, headers?: HttpHeaders): Observable<T> {

        return this.http.post<T>(this.getAPI() + url, data, {
          headers: this.buildHeaders(useAuthHeaders, headers),
          withCredentials: true
        });
    }

    put<T>(url: string, useAuthHeaders: boolean, data: any, headers?: HttpHeaders): Observable<T> {

        return this.http.put<T>(this.getAPI() + url, data, {
          headers: this.buildHeaders(useAuthHeaders, headers),
          withCredentials: true
        });
    }

    delete<T>(url: string, useAuthHeaders: boolean, headers?: HttpHeaders): Observable<T> {

      return this.http.delete<T>(this.getAPI() + url, {
        headers: this.buildHeaders(useAuthHeaders, headers),
        withCredentials: true
      });
    }

    getComments(params?: any): Observable<any> {
        const url = "/api/meta_comments";
        return this.get(url, true, params);
    }

    addComment(param: any, text: string, parentId?: number) {
        let body = {
            ...param,
            comment: {
                comments: text,
                parent_comment_id: parentId || ""
            }
        };

        return this.post(`/api/meta_comments`, true, body);
    }

    getTags(params?: any): Observable<any> {
        const url = "/api/tags";
        return this.get(url, true, params);
    }

    getFilteredVNCDTags(params: {name: string}): Observable<any> {
      const url = "/api/tags/getTags";
      return this.get(url, true, params);
    }

    createNewVNCDTag(tagName: string, tagColor: string): Observable<any> {
      const data = {
        tag: {
          name: tagName,
          configs: {
            color_hex: tagColor
          }
        }
      };
      return this.post(`/api/tags/createTag`, true, data);
    }

    updateTagsInPost(postId: string, tags: string[]) {
      const data = {
        id: postId,
        tags: tags,
      };
      return this.post(`/api/addTags`, true, data);
    }

    getTagsByJid(jid): Observable<any> {
        let url = "/api/taggings";
        const params = {
          product: "vnctalk",
          object_type: "meeting",
          object_ids: jid
        };
        const query = Object.keys(params).map(key => {
          return `${key}=${params[key]}`;
        });
        url += "?" + query.join("&");

        return this.get(url, true);
    }

    createOrUpdateTag(jid, tags) {
        const objects = {"0": {id: jid, tags_list: tags.join(",")}};
        const body = {
          taggings: {
            product: "vnctalk",
            object_type: "meeting",
            objects: objects
          }
        };
        return this.post(`/api/taggings`, true, body);
    }

    createGroup(name: string, contact_ids?: number[]) {
        const data = {
            contact_group: {
              name: name,
              contact_ids: contact_ids || []
            }
        };
        return this.post(VNCTALK_API + `/contact_groups.json`, true, data);
    }

    getContacts(queryParams: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let data: any = { headers: headers };
        if (queryParams) {
            data.params = queryParams;
        }
        return this.http.get(this.getAPI() + VNCTALK_API + "/contacts.json", data);
    }

    getGroups(queryParams: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let data: any = { headers: headers };
        if (queryParams) {
            data.params = queryParams;
        }
        return this.http.get(this.getAPI() + VNCTALK_API + `/contact_groups.json`, data);
    }

    syncMessages(queryParams: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        let options: any = { headers: headers };
        return this.http.post(this.getAPI() + VNCTALK_API + `/sync-messages`, queryParams, options);
    }

    getAroundMessages(queryParams: any): Observable<any> {
        return this.post(VNCTALK_API + `/around-msgid`, true, queryParams);
    }

    sendMessage(data: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        let options: any = { headers: headers };
        if (data.data) {
            return of(data.data.id);
        }
        return this.http.post(this.getAPI() + VNCTALK_API + `/xmpp-rest`, data, options);
    }

    sendMail(data: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        let options: any = { headers: headers };
        return this.http.post(this.getAPI() + VNCTALK_API + `/sendmail`, data, options);
    }

    messageAction(body: any): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        const t1 = new Date().getTime();
        const params = JSON.stringify(body);
        return this.http.post(this.getAPI() + VNCMAIL_API + "/msgAction", body,
            options).pipe(map(this.returnResponse), tap(res => {
                const t2 = new Date().getTime();
                const performance = (t2 - t1) / 1000;

                CommonUtils.sentryLog(`[API][messageAction] took ${performance} s - ${params}`, performance);
            }), catchError(this.auth.handleErrorObservable.bind(this)));
    }

    getAllRoomMembers(): Observable<any> {
        return this.http.get(this.getAPI() + VNCTALK_API + "/roomMembers/all");
    }

    getRoomMembers(target): Observable<any> {
        return this.http.get(this.getAPI() + VNCTALK_API + "/roomMembers/" + target);
    }

    getAudienceList(broadcastId: string): Observable<any> {
        return this.http.get(this.getAPI() + VNCTALK_API +  "/broadcastAudience/" + encodeURIComponent(broadcastId));
    }

    loadMessages(target: string, maxItems: number, offset: number = 0,
        startTimestamp?: number, endTimestamp?: number): Observable<{messages: Message[], total: number}> {
        let data = {};
        data["rows"] = maxItems;
        data["offset"] = 0;
        data["tags"] = [];
        if (target.indexOf("@conference") === -1) {
            data["excludeGroupchat"] = true;
            data["peerMatch"] = target;
        } else {
            data["excludeGroupchat"] = false;
            data["confId"] = target;
            data["roomMatch"] = target;
        }
        if (startTimestamp) {
            data["nb"] = moment(new Date(startTimestamp)).utc().format();
        }
        if (endTimestamp) {
            data["na"] = moment(new Date(endTimestamp)).utc().format();
        }
        if (offset) {
            data["offset"] = offset;
        }
        const response = new Subject<any>();
        this.syncMessages(data).subscribe(res => {
            let messages: Message[] = res.docs.map(message => CommonUtils.convertToXmppMessage(message));
            const deletedMessages = messages.filter(msg => msg.replace).map(msg => msg.replace.id);
            messages = messages.filter(msg => !msg.replace).map(msg => {
                msg.isDeleted = false;
                if (deletedMessages.indexOf(msg.id) !== -1) {
                    msg.isDeleted = true;
                    msg.body = null;
                }
                return msg;
            });
            response.next({ messages: messages, total: res.numFound });
        });
        return response.asObservable().pipe(take(1));
    }

    getContact(contactId: number): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        return this.http.get<any>(this.getAPI() + VNCTALK_API + `/contacts/${contactId}`, { headers: headers });
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error("An error occurred:", error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.error}`);
        }
        // return an observable with a user-facing error message
        return throwError(
            "Something bad happened; please try again later.");
    }


    private mapSearchItem(doc) {
        const content = doc.content_txt ? doc.content_txt[0] : "";
        const raw = doc.raw_txt ? doc.raw_txt[0] : "";
        let parsedContent = CommonUtils.replaceLinkToAnchor(content);
        const rawTxt = raw.replace(/\\"/ig, "\"").replace(/\\'/ig, "\'");
        if (rawTxt !== "") {
            parsedContent = CommonUtils.beautifyMailContent(rawTxt);
        }
        const shortContent = CommonUtils.replaceLinkToAnchor(CommonUtils.limitContent(CommonUtils.HTMLToPlainText(parsedContent), 500));
        let id = doc.id;
        let objectId = doc.id;
        if (doc.type_s === "issue") {
            id = doc.issue_id_i;
        } else if (doc.type_s === "mail") {
            id = doc.mail_id_i;
            objectId = objectId.replace(`.${doc.mail_id_i}`, "");
        } else if (doc.type_s === "talk") {
            objectId = objectId.replace(`.${doc.owner_s}`, "").replace(`talk.${doc.talk_id_s}.`, "");
            objectId = objectId.replace(`talk.`, "");
        }
        return {
            id: id,
            objectId: objectId,
            talkId: doc.talk_id_s,
            owner: doc.owner_s,
            title: doc.title_s ? doc.title_s.replace(/\\"/ig, "\"").replace(/\\'/ig, "\'") : "",
            contentTxt: content.replace(/\r?\n/g, "\\n")
            .replace(/\\n/g, "<br />"),
            parsedContent: parsedContent.replace(/\r?\n/g, "\\n")
            .replace(/\\n/g, "<br />"),
            shortContent: shortContent.replace(/\r?\n/g, "\\n")
            .replace(/\\n/g, "<br />"),
            chatType: doc.talk_type_s,
            rawTxt: raw,
            createdDt: doc.created_dt,
            modifiedDt: doc.modified_dt,
            from: doc.from_s,
            to: doc.to_ss,
            type: doc.type_s,
            version: doc._version_,
            mailFolderID: doc.mail_folder_id_i,
            unread: doc.mail_unread_b,
            mailAttachments: this.getMailAttachments(doc),
            flags: doc.mail_flags_s,
            tags: doc.tags_ss,
            solrId: doc.id,
        } as SearchItem;
    }

    mapTalkGroupIntoSearchItem(group, contacts: {email: string, name: string}[]) {

      const doc = group.doclist.docs[0];
      let groupedContent = "";
      let chatType = "chat";
      let title = group.groupValue;
      if (group.groupValue.indexOf("@conference.") !== -1) {
        chatType = "groupchat";
      } else if (group.groupValue.indexOf("broadcast") !== -1) {
        chatType = "broadcast";
      }
      let sender = doc.from_s;
      if (chatType === "chat") {
        sender = group.groupValue.split("-")[1];
        let user = title.split("-")[1];
        if (this.loggedInUser.email ===  user) {
            user =  title.split("-")[0];
          }
        let userList = JSON.parse(localStorage.getItem("saved_state"));
        if (userList) {
            user = userList.contactState.entities[user];
            if (user) {
              title = user.name;
            }
        }
        user = userList.contactState[user];
      }

      group.doclist.docs.map( d => {
        let senderName = "";
        const sender = contacts.find(c => c.email === d.from_s);
        let userSpan = `<span class="user-name non-capital">${d.from_s}</span>`;
        if (!!sender) {
          senderName = sender.name;
          userSpan = `<span class="user-name">${senderName}</span>`;
        }



        const content = d.content_txt ?
          `<div class="one-message">` + userSpan + `: ${d.content_txt[0]} </div> ` : "";
        if (!!content) {
          groupedContent = groupedContent + content + "<br>";
        }
      });

      // const content = doc.content_txt ? doc.content_txt[0] : "";
      const raw = doc.raw_txt ? doc.raw_txt[0] : "";
      // TODO change raw txt to be combo of 5 text messages
      let parsedContent = CommonUtils.replaceLinkToAnchor(groupedContent);
      // const rawTxt = raw.replace(/\\"/ig, "\"").replace(/\\'/ig, "\'");
      // if (rawTxt !== "") {
      //   parsedContent = CommonUtils.beautifyMailContent(rawTxt);
      // }
      const shortContent = CommonUtils.replaceLinkToAnchor(CommonUtils.limitContent(CommonUtils.HTMLToPlainText(parsedContent), 500));
      let id = doc.id;
      let objectId = doc.id;
      objectId = objectId.replace(`.${doc.owner_s}`, "").replace(`talk.${doc.talk_id_s}.`, "");
      objectId = objectId.replace(`talk.`, "");

      return {
        id: id,
        objectId: objectId,
        talkId: doc.talk_id_s,
        owner: doc.owner_s,
        title,
        contentTxt: groupedContent.replace(/\r?\n/g, "\\n")
          .replace(/\\n/g, "<br />"),
        parsedContent: parsedContent.replace(/\r?\n/g, "\\n")
          .replace(/\\n/g, "<br />"),
        shortContent: shortContent.replace(/\r?\n/g, "\\n")
          .replace(/\\n/g, "<br />"),
        chatType: chatType,
        rawTxt: raw,
        createdDt: doc.created_dt,
        modifiedDt: doc.updated_dt,
        from: sender,
        to: doc.to_ss,
        type: doc.type_s,
        version: doc._version_
      } as SearchItem;
    }

    private getMailAttachments(doc) {
        if (!doc.mail_attachments_s) {
            return [];
        }
        const attachments = JSON.parse(doc.mail_attachments_s);
        return attachments.map(v => {
            return {
                id: v.id.replace(doc.mail_message_id_s, "").replace("_", ""),
                name: v.name_s,
                type: v.type_s,
                contentType: v.contenttype_s,
                contentLength: v.contentlength_i,
                parentId: v.parent_id_s,
                part: v.part_s
            };
        });
    }

    public renderEmoji(input: string): SafeHtml {
        return this.domSanitizer.bypassSecurityTrustHtml(wdtEmojiBundle.render(input));
    }

    private returnResponse(res: Response): Response {
        return res;
    }

    uploadFile(url, file): Observable<HttpEvent<any>> {
        const headers = new HttpHeaders().set("Content-Type", "application/octet-stream");
        const req = new HttpRequest("PUT", url, file, {
          headers: headers,
          reportProgress: true,
        });
        return this.http.request(req);
    }

    getMailFolders(view = "message, conversation"): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        return this.http.post(this.getAPI() + VNCMAIL_API + "/getFolderList", {view: view}, options);
    }

    searchRequest(body: SearchRequest): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        return this.http.post(this.getAPI() + VNCMAIL_API + "/searchRequest", body, options);
    }

    getAutoCompleteList(inputValue: string): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        const body = { name: inputValue };
        return this.http.post(this.getAPI() + VNCMAIL_API + "/autocomplete",
        {name: inputValue}, options)
        .pipe(map(this.returnResponse), catchError(this.auth.handleErrorObservable.bind(this)));
    }

    saveMailAsDraft(saveSendMessage: SaveSendMessage): Observable<any> {
        const t1 = new Date().getTime();
        const params = JSON.stringify(saveSendMessage);
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        return this.http.post(this.getAPI() + VNCMAIL_API + "/saveDraft",
            saveSendMessage, options).pipe(map(this.returnResponse), tap(res => {
              const t2 = new Date().getTime();
              const performance = (t2 - t1) / 1000;

              CommonUtils.sentryLog(`[API][saveDraft] took ${performance} s - ${params}`, performance);
            }),
            catchError(this.auth.handleErrorObservable.bind(this)));
      }

    sendEmail(saveSendMessage: SaveSendMessage): Observable<any> {
        const t1 = new Date().getTime();
        const params = JSON.stringify(saveSendMessage);
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        return this.http.post(this.getAPI() + VNCMAIL_API + "/sendEmail",
            saveSendMessage, options).pipe(tap(res => {
              const t2 = new Date().getTime();
              const performance = (t2 - t1) / 1000;

              CommonUtils.sentryLog(`[API][sendEmail] took ${performance} s - ${params}`, performance);
            }),
            map(this.returnResponse), catchError(this.auth.handleErrorObservable.bind(this)));
      }

      uploadAttachment(file: any): Observable<any> {
        const formData = new FormData();

        formData.append("file", file);
        const t1 = new Date().getTime();
        let headers: HttpHeaders = new HttpHeaders();
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Authorization": token });
        }
        return this.http.post(this.getAPI() + "/api/vncmail_upload", formData,
        {
            headers: headers,
            responseType: "text"
        }).pipe(tap(res => {

              const t2 = new Date().getTime();
              const performance = (t2 - t1) / 1000;

              CommonUtils.sentryLog(`[API][uploadAttachment] took ${performance} s for API call`, performance);
            }));
      }

      updateGroupAvatar(target, base64Data: string) {
        const data = { "avatar_base64": "data:image/png;base64," + base64Data };
        let headers = new HttpHeaders({ "Content-Type": "text/plain;charset=UTF-8" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "text/plain;charset=UTF-8", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        return this.http.post(this.getAPI() + "/api/group_chats/avatar/" + target, data, options);
      }

      updateGroupInfo(target, params) {
        const data = { "group_chat": params};
        return this.post(`/api/group_chats/${target}`, true, data);
      }

      getMsgRequest(id: string): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };

        const t1 = new Date().getTime();
        return this.http.post(this.getAPI() + VNCMAIL_API + "/getMsgRequest", { id }, options)
        .pipe(tap(res => {

            const t2 = new Date().getTime();
            const performance = (t2 - t1) / 1000;

            CommonUtils.sentryLog(`[API][getMsgRequest] took ${performance} s - ID=${id}`, performance);
        }));
    }

    getPreferences(): Observable<any> {
      let headers = new HttpHeaders({ "Content-Type": "application/json" });
      if (this.isCordovaOrElectron) {
          const token = localStorage.getItem("token");
          headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
      }
      let options: object = {
          headers: headers
      };
      const t1 = new Date().getTime();
      return this.http.post(this.getAPI() + VNCMAIL_API + "/getPrefs", { }, options)
        .pipe(tap(res => {
            const t2 = new Date().getTime();
            const performance = (t2 - t1) / 1000;
        }));
  }

    getSignatures(): Observable<any> {
      console.log("[PreferenceService][getSignatures]");
      const ZIMBRA_ACCOUNT = "urn:zimbraAccount";
      const request = {
          GetSignaturesRequest: {
              "@": {
                  "xmlns": ZIMBRA_ACCOUNT,
                  "requestId": "0"
              }
          }
      };

      let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };

        const t1 = new Date().getTime();
        return this.http.post(this.getAPI() + VNCMAIL_API + "/batchRequest", request, options)
        .pipe(tap(res => {

            const t2 = new Date().getTime();
            const performance = (t2 - t1) / 1000;
        }));

  }

    getAttributes (section: string): Observable<any> {
        let headers = new HttpHeaders({ "Content-Type": "application/json" });
        if (this.isCordovaOrElectron) {
            const token = localStorage.getItem("token");
            headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
        }
        let options: object = {
            headers: headers
        };
        const t1 = new Date().getTime();
        return this.http.get(this.getAPI() + VNCMAIL_API + "/getInfo?sections=" + section , options)
        .pipe(tap(res => {
          const t2 = new Date().getTime();
          const performance = (t2 - t1) / 1000;

          CommonUtils.sentryLog(`[API][getInfo]sections=${section} took ${performance} s for API call`, performance);
        }));
    }

    getProjectMembers(projectId: number): Observable<TaskUser[]> {
      return this.get(VNCTASK_API + "/task/projects/" + projectId + "/memberships.json?" + "only_user_details=1&limit=1000", true)
        .pipe(map((res: any) => res.users.map(json => new TaskUser(json))));
    }

    changeTaskCompletedStatus(taskId: string, completed: boolean) {
      let redmineStatus = {new: 1, inProgress: 2, completed: 18};
      let payload: any  = {
        issue: {
          status_id: completed ? redmineStatus.completed : redmineStatus.inProgress
        }
      };
      let headers = new HttpHeaders({ "Content-Type": "application/json" });
      return this.put(VNCTASK_API + "/task/issues/" + taskId + ".json?include=attachments,tags", true,
        payload, headers
      );
    }

    addNewTask(payload: any): Observable<any> {
      return this.post(VNCTASK_API + "/task/issues.json?include=tags,list,invitation_details", true, {issue: payload});
    }

    getTaskDetails(id: number): Observable<any> {
      return this.get<string>(VNCTASK_API + "/task/issues/" + id + ".json?include=journals,attachments,tags", true);
    }

    getTicketList(): Observable<any> {

      return this.get<string>(VNCTASK_API + "/task/issues.json?include=journals,attachments,tags,watchers&file_access_token=1&assigned_to_id=me", true);
    }

    getTicketPreview(issueID): Observable<any> {
      return this.get<string>(VNCTASK_API + `/task/issues/${issueID}.json`, true);
    }

    addWatcherInTicket(ticketId,payload: {user_id:string}): Observable<any> {
      return this.post(VNCTASK_API + `/task/issues/${ticketId}/watchers.json`, true, payload);
    }

    removeWatcherInTicket(ticketId,userId): Observable<any> {
      return this.delete(VNCTASK_API + `/task/issues/${ticketId}/watchers/${userId}.json`, true);
    }

    getCurrentTicketUser() {
      return this.get<string>(VNCTASK_API + "/task//users/current.json", true)
    }

    getTaskList(trackerId:any): Observable<any> {
      const params = new URLSearchParams();
      params.append("[tracker_id][]", trackerId.toString());
       let ss = `/task/issues.json?include=journals,attachments,tags,list,invitation_details,location,spent_hours,watchers&sort=due_date:asc&f%5B%5D=author_or_assigned&f%5B%5D=status_id&f%5B%5D=tracker_id&f%5B%5D=project_id&op%5Bauthor_or_assigned%5D==&v%5Bauthor_or_assigned%5D%5B%5D=me&op%5Bstatus_id%5D=o&op%5Btracker_id%5D==&v${params.toString()}&op%5Bproject_id%5D==&v%5Bproject_id%5D%5B%5D=mine&limit=25`
       return this.get<string>(VNCTASK_API + ss, true);
      // return this.get<string>(VNCTASK_API + `/task/issues.json?include=journals,attachments,tags,list,invitation_details,location,spent_hours,watchers&sort=due_date:asc&f%5B%5D=author_or_assigned&f%5B%5D=status_id&f%5B%5D=tracker_id&f%5B%5D=project_id&op%5Bauthor_or_assigned%5D==&v%5Bauthor_or_assigned%5D%5B%5D=me&op%5Bstatus_id%5D=*&op%5Btracker_id%5D==&v${params.toString()}&op%5Bproject_id%5D==&v%5Bproject_id%5D%5B%5D=mine`, true);
    }

    getTracker () {
      return this.get(VNCTASK_API + "/task/trackers.json", true);
    }

    editTask(taskId: number, payload: any): Observable<any> {
      return this.put(VNCTASK_API + "/task/issues/" + taskId + ".json?include=tags,list,invitation_details", true, {issue: payload});
    }

    deleteTask(taskId: number) {
      const selectedIds = [taskId];
      return this.delete(VNCTASK_API + "/task/vnctasks/delete.json?ids[]=" + selectedIds.join("&ids[]="), true);
    }

    /**
     *
     * We need to fetch the records paginated, pass the lastVisible param returned from
     * response in the request to get the next set of results.
     */
    getSavedSearches(): Observable<any> {
      return this.get<any>("/api/savedSearch/get", true).pipe(map(res => {
        if (res === null) {
          return [];
        }
        return res.data.map( doc => {
          const name = doc.name_s;
          const searchTerm = doc.search_term_txt;
          const filters = JSON.parse(doc.filters_txt);
          return {id: doc.id, name, searchTerm, filters};
        });
      }));
    }

    addSearch(body: any): Observable<any> {
      return this.post(`/api/savedSearch/create`, true, body);
    }

    updatedSearch(body: any): Observable<any> {
      return this.put(`/api/savedSearch/update`, true, body);
    }

    deleteSearch(searchId: any): Observable<any> {
      let body = {
        id: searchId
      };
      return this.put(`/api/savedSearch/delete`, true, body);
    }

    addNewTicket(payload: any): Observable<any> {
      return this.post("/api/tickets", true, {issue: payload});
    }


    getTicketDetails(id: number): Observable<any> {
      return this.get("/api/tickets/" + id, true);
    }

    editTicket(id: number, payload: any): Observable<any> {
      return this.put("/api/tickets/" + id, true, {issue: payload});
    }

    addNewIncident(payload: any): Observable<any> {
      return this.post("/api/incidents", true, {issue: payload});
    }

    getIncidentDetails(id: number): Observable<any> {
      return this.get("/api/incidents/" + id, true);
    }

    editIncident(id: number, payload: any): Observable<any> {
      return this.put("/api/incidents/" + id, true, {issue: payload});
    }

    private getMapContacts(contactItem: any): ContactInfo {
        const contact = new ContactInfo();
        contact.id = contactItem.id;
        if (contactItem.addresses && contactItem.addresses !== null && contactItem.addresses.length > 0) {
            contact.address = contactItem.addresses;
        }
        if (contactItem.avatar) {
            contact.avatar = contactItem.avatar;
        }
        if (contactItem.company) {
            contact.company = contactItem.company;
        }
        if (contactItem.created_at && contactItem.created_at !== null) {
            contact.created_at = new Date(contactItem.created_at);
        }
        if (contactItem.deleted_at && contactItem.deleted_at !== null) {
            contact.deleted_at = new Date(contactItem.deleted_at);
        }
        if (contactItem.emails) {
            contact.emails = contactItem.emails;
        }
        if (contactItem.first_name) {
            contact.firstName = contactItem.first_name;
        }
        if (contactItem.groups) {
            contact.groups = contactItem.groups;
        }
        if (contactItem.is_company) {
            contact.is_company = contactItem.is_company === "true" ? true : false;
        }
        if (contactItem.is_global) {
            contact.is_global = contactItem.is_global === "true" ? true : false;
        }
        if (contactItem.jid) {
            contact.jid = contactItem.jid;
        }
        if (contactItem.job_title) {
            contact.jobTitle = contactItem.job_title;
        }
        if (contactItem.last_name) {
            contact.lastName = contactItem.last_name;
        }
        if (contactItem.middle_name) {
            contact.middleName = contactItem.middle_name;
        }
        if (contactItem.phones) {
            contact.phones = contactItem.phones;
        }
        if (contactItem.updated_at && contactItem.updated_at !== null) {
            contact.updated_at = new Date(contactItem.updated_at);
        }
        if (contactItem.is_global) {
            contact.is_global = contactItem.is_global === "true" ? true : false;
        }
        if (contactItem.notes) {
            contact.notes = contactItem.notes;
        }
        if (contactItem.im_accounts && contactItem.im_accounts !== null && contactItem.im_accounts.length > 0) {
            contact.im_accounts = contactItem.im_accounts;
        }
        if (contactItem.urls) {
            contact.urls = contactItem.urls;
        }
        if (contactItem.custom_fields) {
            contact.custom_fields = contactItem.custom_fields;
        }
        if (contactItem.events && contactItem.events !== null && contactItem.events.length > 0) {
            contact.events = contactItem.events;
        }
        if (contactItem.contact_lists) {
            contact.contact_list = contactItem.contact_lists;
        }
        if (contactItem.tags) {
            contact.tags = contactItem.tags;
        }
        if (contactItem.time_zone && contactItem.time_zone !== null) {
            contact.timezone = contactItem.time_zone;
        }
        if (contactItem.language && contactItem.language !== null) {
            contact.language = contactItem.language;
        }
        if (contactItem.skills && contactItem.skills !== null && contactItem.skills.length > 0) {
            contact.skills = contactItem.skills;
        }
        if (contactItem.interests && contactItem.interests !== null && contactItem.interests.length > 0) {
            contact.interests = contactItem.interests;
        }
        if (contactItem.birthday && contactItem.birthday !== null) {
            contact.birthday = new Date(contactItem.birthday);
        }
        if (contactItem.gender && contactItem.gender !== null) {
            contact.gender = contactItem.gender;
        }
        if (contactItem.marital_status && contactItem.marital_status !== null) {
            contact.marital_status = contactItem.marital_status;
        }
        if (contactItem.private_email && contactItem.private_email !== null) {
            contact.private_email = contactItem.private_email;
        }
        if (contactItem.start_date && contactItem.start_date !== null) {
            contact.start_date = new Date(contactItem.start_date);
        }
        if (contactItem.end_date && contactItem.end_date !== null) {
            contact.end_date = new Date(contactItem.end_date);
        }
        if (contactItem.per_week_availability && contactItem.per_week_availability !== null) {
            contact.per_week_availability = contactItem.per_week_availability;
        }
        if (contactItem.hourly_rate && contactItem.hourly_rate !== null) {
            contact.hourly_rate = contactItem.hourly_rate;
        }
        if (!!contactItem.vnc_employee && contactItem.vnc_employee !== null) {
            contact.vnc_employee = contactItem.vnc_employee;
        }
        if (contactItem.payment_mode && contactItem.payment_mode !== null) {
            contact.payment_mode = contactItem.payment_mode;
        }
        if (contactItem.passport_expiry && contactItem.passport_expiry !== null) {
            contact.passport_expiry = new Date(contactItem.passport_expiry);
        }
        if (contactItem.rfc_limit && contactItem.rfc_limit !== null) {
            contact.rfc_limit = contactItem.rfc_limit;
        }
        if (contactItem.username && contactItem.username !== null) {
            contact.username = contactItem.username;
        }
        if (contactItem.admin && contactItem.admin !== null) {
            contact.admin = contactItem.admin;
        }
        if (contactItem.agb_accepted && contactItem.agb_accepted !== null) {
            contact.agb_accepted = contactItem.agb_accepted;
        }
        if (contactItem.video_bridge && contactItem.video_bridge !== null) {
            contact.video_bridge = contactItem.video_bridge;
        }
        if (contactItem.omemo && contactItem.omemo !== null) {
            contact.omemo = contactItem.omemo;
        }
        if (contactItem.id_number && contactItem.id_number !== null) {
            contact.national_id_number = contactItem.id_number;
        }
        if (contactItem.id_expiry && contactItem.id_expiry !== null) {
            contact.national_id_expiry = new Date(contactItem.id_expiry);
        }
        if (contactItem.products && contactItem.products !== null && contactItem.products.length > 0) {
            contact.products = contactItem.products;
        }
        if (contactItem.favorite && contactItem !== null) {
            contact.favorite = contactItem.favorite === "true" ? true : false;
        }
        contact.fullName = this.getFullName(contact.firstName, contact.lastName);
        contact.bgAvatarColor = CommonUtils.getRandomAvatarColor();
        return contact;
    }
    private getFullName(firstName: string, lastName: string): string {
        if (firstName && lastName) {
            return firstName + " " + lastName;
        } else {
            if (firstName && firstName !== undefined) {
                return firstName;
            } else if (lastName && lastName !== undefined) {
                return lastName;
            }
        }
    }

    getSuggestions(query: string): Observable<any[]> {
      const data = {query};
      return this.post<any>(`/api/suggest`, true, data).pipe(
        map(v => {
          const vncSuggest = v.suggest.vncSuggest;
          if (!!vncSuggest && vncSuggest[query]) {
            return vncSuggest[query].suggestions;
          } else {
            return [];
          }
        })
      );
    }

    saveFCMToken(token: string, email: string) {
      let body = {
        email: email,
        token: token,
      };
      return this.post(`/api/search/saveToken`, true, body);
    }

    getEnglishJson() {
      return this.http.get("assets/i18n/en.json");
    }

    getGermanJson() {
      return this.http.get("assets/i18n/de.json");
    }

    archiveConversation(conv: any): Observable<any> {
      return this.updateConversationStatus(conv, {
        archived: true
      });
    }

    private updateConversationStatus(conv: any, data: {
      archived?: boolean,
      state?: string
    }, lastConvoTimestamp?: number): Observable<any> {
      const timestamp = lastConvoTimestamp ? lastConvoTimestamp : new Date().getTime();
      const conversationObject = {
        "Target": conv.Target,
        "Timestamp": Math.ceil(timestamp / 1000),
        "Content": JSON.stringify({
          content: conv.content || "",
          type: conv.type,
          archived: data.archived || conv.archived,
          state: data.state || conv.state,
        }),
        "unreadids": conv.unreadIds,
        "mute_notification": conv.mute_notification,
        "broadcast_title": conv.broadcast_title,
        "mute_sound": conv.mute_sound,
      };
      let headers = new HttpHeaders({ "Content-Type": "application/json" });
      if (this.isCordovaOrElectron) {
          const token = localStorage.getItem("token");
          headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
      }
      return this.http.post<any>("/api/vnctalk/conversationHistory", conversationObject, { headers: headers }) .pipe(map(() => this.mapMiddlewareConversationToConversation(conversationObject)));
    }

  private mapMiddlewareConversationToConversation(conversation: any): Conversation {
    const contentObject = JSON.parse(conversation.Content);
    let content = CommonUtils.unEscapeHTMLString(contentObject.content);
    let timestamp = conversation.Timestamp * 1000;
    if (timestamp === 0 && conversation.updated_at) {
      timestamp = conversation.updated_at * 1000;
      contentObject.has_data = true;
    }
    let type = "chat";
    if (!!localStorage.getItem("conferenceDomain")) {
      type = conversation.Target.indexOf(localStorage.getItem("conferenceDomain")) !== -1 ? "groupchat" : "chat";
    }
    let conv: Conversation = {
      Target: conversation.Target,
      type: contentObject.type ? contentObject.type : type,
      // Adding false as default since all the conv might not have this payload
      archived: contentObject.archived || false,
      state: contentObject.state,
      is_persistent: contentObject.mucpersistent || false,
      incoming: contentObject.incoming || false,
      received_receipt: contentObject.received_receipt || false,
      // read: contentObject.read || false,
      Timestamp: timestamp, // PRASHANT_COMMENT fix 10/13 digit issue between middleware and xmpp
      content: content,
      displayName: contentObject.displayname,
      groupChatTitle: contentObject.groupChatTitle || contentObject.displayname,
      unreadIds: conversation.unreadids,
      mute_sound: conversation.mute_sound,
      mute_notification: conversation.mute_notification,
      deleted: conversation.deleted,
      conferenceType: contentObject.x_vncConference ? contentObject.x_vncConference.conferenceType : "chat"
    };
    if (conversation?.pinorder !== undefined && conversation?.pinorder !== null) {
      conv.pinorder = conversation.pinorder;
    }
    if (conversation.broadcast_title) {
      conv.broadcast_title = conversation.broadcast_title;
    }
    if (conversation.bc_owner) {
      conv.broadcast_owner = conversation.bc_owner;
    }
    if (conversation.total_mentions) {
      conv.total_mentions = conversation.total_mentions;
    }
    if (conversation.unread_mentions) {
      conv.unread_mentions = conversation.unread_mentions;
    }
    return conv;
  }

  toggleConversationFavorite(conv: Conversation, flag?: boolean): Observable<Conversation> {
    const signal = {
        type: "favourite",
        data: JSON.stringify(flag),
        target: conv.Target
      };
    this.xmppService.sendSignalToMyself(signal);

    let headers = new HttpHeaders({ "Content-Type": "application/json" });
    if (this.isCordovaOrElectron) {
        const token = localStorage.getItem("token");
        headers = new HttpHeaders({ "Content-Type": "application/json", "Authorization": token });
    }
    return this.http.post<Conversation>(this.getAPI() + "/api/vnctalk/setfavchat", {target: conv.Target, isfav: flag}, { headers: headers });
  }
    getMailData(): Observable<any> {
      const body = {
          limit: 50,
          needExp: 1,
          offset: 0,
          originalQuery: "in:inbox",
          query: "in:inbox",
          recip: "0",
          sortBy: "dateDesc",
          types: "message"
        }
      const url = VNCMAIL_API + "/searchRequest";
      return this.post(url, true, body);
    }

    deleteMail(id,l) {
      const body = {
        id : id,
        l : l,
        op : "trash"
      }
      const url = VNCMAIL_API + "/msgAction";
      return this.post(url, true, body);
    }

    markUnreadMail(id, operation) {
      const body = {
        id : id,
        op : operation
      }
      const url = VNCMAIL_API + "/msgAction";
      return this.post(url, true, body);
    }

    markFavoriteMail(id, operation) {
      const body = {
        id : id,
        op : operation
      }
      const url = VNCMAIL_API + "/msgAction";
      return this.post(url, true, body);
    }

    markSpamMail(id) {
      const body = {
        id : id,
        op : "spam"
      }
      const url = VNCMAIL_API + "/msgAction";
      return this.post(url, true, body);
    }

    getMailContent(id) {
      const body = {
        id : id,
      }
      const url = VNCMAIL_API + "/getMsgRequest";
      return this.post(url, true, body);
    }

    getEventList(): Observable<any> {
      const beginDate = new Date();
      return this.getMailFolders("appointment").pipe(take(1), switchMap(v => {
        let query = "inid:\"10\"";
        const folder = [];
        if (v && v.folder && v.folder[0] && (v.folder[0].folder || v.folder[0]?.link)) {
          if (v.folder[0]?.folder) {
            folder.push(...v.folder[0]?.folder);
          }
          if (v.folder[0]?.link) {
            folder.push(...v.folder[0]?.link);
          }
          query = this.getShareFolderQuery(folder || []);
        }
        const payload = {
          calExpandInstStart: moment(beginDate).clone().startOf("month").subtract(6, "day").format("x"),
          calExpandInstEnd: moment(beginDate).clone().endOf("month").add(6, "day").format("x"),
          limit: 100,
          offset: 0,
          query: query,
          fetch: "all",
          types: "appointment"
        };
        return this.post(this.getAPI() + "/api/vncmail/searchRequest", true, payload);
      }));

    }

  getShareFolderQuery(folders): string {
    const ids: string [] = [];
    const allFolders = [...folders , ...CommonUtils.getChildFolders(folders)];
    folders = allFolders;
    folders.filter(folder => folder.f && folder.f.indexOf("#") !== -1).map( f => {
      if (f.perm) {
        if (f.id.indexOf(":") !== -1 ) {
          ids.push("inid:" + "\"" + f.id + "\"");
        } else {
          ids.push("inid:" + f.id);
        }
        const children = CommonUtils.getChildFolders([f]);
        if (children.length > 0) {
          children.map( c => {
            ids.push("inid:" + "\"" + c.id + "\"");
          });
        }
      } else {
        ids.push("inid:" + "\"" + f.id + "\"");
        const children = CommonUtils.getChildFolders([f]);
        if (children.length > 0) {
          children.map( c => {
            if (c.f && c.f.indexOf("#") !== -1) {
              ids.push("inid:" + "\"" + c.id + "\"");
            }
          });
        }
      }
    });
    return ids.join(",").replace(/,/g, " OR ");
  }

    public prepareConversationContent(content: string): SafeHtml {
      const REPLY_MESSAGE_LINE_BREAK = "\n-------------\n";
      if (!content) {
        return "";
      }
      const repliedMessage = content.split(REPLY_MESSAGE_LINE_BREAK);
      if (repliedMessage.length > 1) {
        content = repliedMessage[1];
      }
      return this.renderEmoji(this.sanitizeHTML(content).replace(/&amp;nbsp;/ig, " ").replace(/&lt;/ig, "<")
      .replace(/&gt;/ig, ">").replace(/&amp;lt;/ig, "&lt;").replace(/&amp;gt;/ig, "&gt;")
      .replace(/&quote;/g, "\"")
      .replace(/&trade;/g, "™")
      .replace(/&copy;/g, "©")
      .replace(/&amp;/ig, "&")
      .replace(/&amp;amp;quote;/g, "&quote;")
      .replace(/&amp;amp;trade;/g, "&trade;")
      .replace(/&amp;amp;copy;/g, "&copy;")
      .replace(/&amp;amp;/ig, "&amp;")
      );
    }

    public sanitizeHTML(inputHTML: string): string {
      const escapedHTML = CommonUtils.escapeHTMLString(inputHTML);
      return this.parseLinkToHumanreadable(escapedHTML);
    }



  private _addPrefixForMessageWithLink(content): string {
    content = content.trim();
    let newContent = content;
    if (content.startsWith("http") || content.startsWith("https")) {
      const link = content.replace(/https?:\/\//ig , "").toLocaleLowerCase();
      newContent = "==link:" + content;
      if (link.split("/").length > 1) {
        const fileName = link.split("/").pop();
        if (fileName.split(".").length > 1) {
          const extension = fileName.split(".").pop();
          if (CommonUtils.isImage(extension)) {
            newContent = "==foto:" + content;
          } else if (CommonUtils.isAudio(extension)) {
            if (fileName.startsWith("audio_recording_")) {
              newContent = "==voicemessage:" + content;
            } else {
              newContent = "==audio:" + content;
            }
          } else if (CommonUtils.isVideo(extension)) {
            newContent = "==video:" + content;
          }
        }
      }
    }
    return newContent;
  }


  public parseLinkToHumanreadable(text: string): string {
    text = this._addPrefixForMessageWithLink(text);

    // this.logger.info("parseLinkToHumanreadable", text);

    let translation;
    if (this.configService.getConfiguredLang() !== "de") {
      translation = {
        PHOTO: "Photo",
        VOICE_MESSAGE: "Voice Message",
        "LINK": "Link",
        AUDIO: "Audio",
        VIDEO_EXTENSION: "Video",
        MISSED_CALL: "Missed call",
        ENDED_CALL: "Ended call",
        UPLOADING_FILE: "Uploading.."
      };
    } else {
      translation = {
        PHOTO: "Foto",
        VOICE_MESSAGE: "Sprachnachricht",
        "LINK": "Verkn\u00fcpfung",
        AUDIO: "Sprachanruf",
        VIDEO_EXTENSION: "Video",
        MISSED_CALL: "Missed call",
        ENDED_CALL: "Ended call",
        UPLOADING_FILE: "Wird hochgeladen.."
      };
    }

    if (text.startsWith("blob:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>Icon/product/attachment</title>
        <g id="Icon/product/attachment" stroke="none" stroke-width="1" fill-rule="evenodd">
            <g id="Icon-24px" transform="translate(2.000000, 7.000000)" >
                <path d="M0,5.5 C0,2.46 2.46,0 5.5,0 L16,0 C18.21,0 20,1.79 20,4 C20,6.21 18.21,8 16,8 L7.5,8 C6.12,8 5,6.88 5,5.5 C5,4.12 6.12,3 7.5,3 L15,3 L15,5 L7.41,5 C6.86,5 6.86,6 7.41,6 L16,6 C17.1,6 18,5.1 18,4 C18,2.9 17.1,2 16,2 L5.5,2 C3.57,2 2,3.57 2,5.5 C2,7.43 3.57,9 5.5,9 L15,9 L15,11 L5.5,11 C2.46,11 0,8.54 0,5.5 L0,5.5 Z" id="Shape"></path>
            </g>
        </g>
      </svg>`
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-attachment">${svgIcon}</span>${translation.UPLOADING_FILE}</div>`;
      this.translate.get("UPLOADING_FILE")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-attachment">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==foto:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>Icon/product/img-new</title>
        <g id="Icon/product/img-new" stroke="none" stroke-width="1"  fill-rule="evenodd">
            <path d="M19,3.5 C20.6568542,3.5 22,4.84314575 22,6.5 L22,6.5 L22,18.5 C22,20.1568542 20.6568542,21.5 19,21.5 L19,21.5 L5,21.5 C3.34314575,21.5 2,20.1568542 2,18.5 L2,18.5 L2,6.5 C2,4.84314575 3.34314575,3.5 5,3.5 L5,3.5 Z M15.121,12.756 L11.7452122,17.3218537 C11.4357207,17.7403706 10.8629658,17.846786 10.4275814,17.5854256 L10.329729,17.5185577 L7.916,15.654 L4.88337887,19.4932723 L4.88337887,19.4932723 L5,19.5 L19,19.5 C19.0439873,19.5 19.0873112,19.4971599 19.1297985,19.4916531 L15.121,12.756 Z M19,5.5 L5,5.5 C4.44771525,5.5 4,5.94771525 4,6.5 L4,6.5 L4,17.394 L6.99595535,13.6331785 C7.3137239,13.2512143 7.85795191,13.1646884 8.2747999,13.4125012 L8.37615338,13.4814423 L10.744,15.311 L14.3724349,10.405419 C14.76186,9.87881006 15.5321201,9.86919209 15.9396018,10.353342 L16.0160407,10.4567487 L20,17.064 L20,6.5 C20,5.98716416 19.6139598,5.56449284 19.1166211,5.50672773 L19,5.5 Z" id="Combined-Shape"></path>
        </g>
      </svg>`
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-photo">${svgIcon}</span>${translation.PHOTO}</div>`;
      this.translate.get("PHOTO")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-photo">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==link:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>Icon/product/insert-link</title>
        <g id="Icon/product/insert-link" stroke="none" stroke-width="1" fill-rule="evenodd">
            <g id="Icon-24px" transform="translate(2.000000, 7.000000)">
                <path d="M1.9,5 C1.9,3.29 3.29,1.9 5,1.9 L9,1.9 L9,0 L5,0 C2.24,0 0,2.24 0,5 C0,7.76 2.24,10 5,10 L9,10 L9,8.1 L5,8.1 C3.29,8.1 1.9,6.71 1.9,5 L1.9,5 Z M6,6 L14,6 L14,4 L6,4 L6,6 L6,6 Z M15,0 L11,0 L11,1.9 L15,1.9 C16.71,1.9 18.1,3.29 18.1,5 C18.1,6.71 16.71,8.1 15,8.1 L11,8.1 L11,10 L15,10 C17.76,10 20,7.76 20,5 C20,2.24 17.76,0 15,0 L15,0 Z" id="Shape"></path>
            </g>
        </g>
      </svg>`
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-link">${svgIcon}</span>${translation.LINK}</div>`;
      this.translate.get("LINK")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container con-type-link">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==voicemessage:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <title>A111215C-7164-47E7-BC4C-29892FDFCEB7</title>
        <g id="Symbols" stroke="none" stroke-width="1" fill-rule="evenodd">
            <g id="Icon/product/voicemessge-new-01">
                <path d="M14.5,6 C15.3284271,6 16,6.67157288 16,7.5 L16,17.5 C16,18.3284271 15.3284271,19 14.5,19 C13.6715729,19 13,18.3284271 13,17.5 L13,7.5 C13,6.67157288 13.6715729,6 14.5,6 Z M9.5,7 C10.3284271,7 11,7.67157288 11,8.5 L11,16.5 C11,17.3284271 10.3284271,18 9.5,18 C8.67157288,18 8,17.3284271 8,16.5 L8,8.5 C8,7.67157288 8.67157288,7 9.5,7 Z M19.5,9 C20.3284271,9 21,9.67157288 21,10.5 L21,14.5 C21,15.3284271 20.3284271,16 19.5,16 C18.6715729,16 18,15.3284271 18,14.5 L18,10.5 C18,9.67157288 18.6715729,9 19.5,9 Z M4.5,10 C5.32842712,10 6,10.6715729 6,11.5 L6,13.5 C6,14.3284271 5.32842712,15 4.5,15 C3.67157288,15 3,14.3284271 3,13.5 L3,11.5 C3,10.6715729 3.67157288,10 4.5,10 Z" id="Combined-Shape"></path>
            </g>
        </g>
      </svg>`
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-voice">${svgIcon}</span>${translation.VOICE_MESSAGE}</div>`;
      this.translate.get("VOICE_MESSAGE")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-voice">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==audio:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <title>Icon/product/audiotrack</title>
          <g id="Icon/product/audiotrack" stroke="none" stroke-width="1" fill-rule="evenodd">
              <g id="Icon-24px" transform="translate(6.000000, 3.000000)">
                  <path d="M6,0 L6,9.28 C5.53,9.11 5.03,9 4.5,9 C2.01,9 0,11.01 0,13.5 C0,15.99 2.01,18 4.5,18 C6.81,18 8.7,16.25 8.95,14 L9,14 L9,3 L13,3 L13,0 L6,0 L6,0 Z" id="Shape"></path>
              </g>
          </g>
      </svg>`
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-audiotrack">${svgIcon}</span>${translation.AUDIO}</div>`;
      this.translate.get("AUDIO")
      .pipe(take(1))
        .subscribe(res => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-audiotrack">${svgIcon}</span> ${res} </div>`;
        });
    } else if (text.startsWith("==video:")) {
      const svgIcon =
      `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <title>Icon/product/camera-new</title>
          <g id="Icon/product/camera-new" stroke="none" stroke-width="1" fill-rule="evenodd">
              <path d="M12,5 C14.1421954,5 15.8910789,6.68396847 15.9951047,8.80035966 L16,9 L16,15 C16,17.1421954 14.3160315,18.8910789 12.1996403,18.9951047 L12,19 L5,19 C2.85780461,19 1.10892112,17.3160315 1.00489531,15.1996403 L1,15 L1,9 C1,6.85780461 2.68396847,5.10892112 4.80035966,5.00489531 L5,5 L12,5 Z M12,7 L5,7 C3.9456382,7 3.08183488,7.81587779 3.00548574,8.85073766 L3,9 L3,15 C3,16.0543618 3.81587779,16.9181651 4.85073766,16.9945143 L5,17 L12,17 C13.0543618,17 13.9181651,16.1841222 13.9945143,15.1492623 L14,15 L14,9 C14,7.9456382 13.1841222,7.08183488 12.1492623,7.00548574 L12,7 Z M20.5425836,7.70208143 C21.4144016,7.1180865 22.5945711,7.35141313 23.178566,8.22323106 C23.3123319,8.4229237 23.4065884,8.64556331 23.4597758,8.90930817 L23.4919415,9.11510777 L23.5,9.28064743 L23.5,14.7186411 C23.5,15.7679821 22.649341,16.6186411 21.6,16.6186411 C21.359692,16.6186411 21.1223057,16.5730628 20.8736196,16.4705074 L20.6847641,16.3827296 L20.5427636,16.2973276 L17.8427636,14.4891546 C17.4300426,14.212758 17.1445442,13.7876818 17.0377508,13.2625835 L17.0088423,13.083913 L17,12.9104681 L17,11.0892663 C17,10.5926029 17.1942814,10.1189073 17.5710764,9.73799882 L17.7034184,9.61456165 L17.8425836,9.51070027 L20.5425836,7.70208143 Z M21.5,14.534 L21.501,9.466 L19,11.14 L18.998,12.856 L21.5,14.534 Z" id="Combined-Shape" ></path>
          </g>
      </svg>`
      text = `<div class='message-icon'><span class="svg-icon-container icon-type-video">${svgIcon}</span>${translation.VIDEO_EXTENSION}</div>`;
      this.translate.get("VIDEO_EXTENSION")
      .pipe(take(1))
        .subscribe(v => {
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-video">${svgIcon}</span> ${v} </div>`;
        });
      } else if (text.startsWith("==missed_call:")) {  // template for "missed call"
        const svgIcon =
        `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <title>Icon/product/arrow-call-missed</title>
            <g id="Icon/product/arrow-call-missed" stroke="none" stroke-width="1" fill-rule="evenodd">
                <g id="Icon-24px" transform="translate(3.000000, 7.000000)" >
                    <polygon id="Shape" points="16.59 0 9 7.59 3.41 2 8 2 8 0 0 0 0 8 2 8 2 3.41 9 10.41 18 1.41"></polygon>
                </g>
            </g>
        </svg>`
        text = `<div class='message-icon'><span class="svg-icon-container icon-type-missed-call">${svgIcon}</span>${translation.MISSED_CALL}</div>`;
        this.translate.get("MISSED_CALL")
        .pipe(take(1))
          .subscribe(res => {
            text = `<div class='message-icon'><span class="svg-icon-container icon-type-missed-call">${svgIcon}</span> ${res} </div>`;
          });
        } else if ( text.startsWith("==ended_call:")) {  // template for "ended call"
          const svgIcon =
          `<svg width="16px" height="16px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
              <title>Icon/product/phone-call-hang-new</title>
              <g id="Icon/product/phone-call-hang-new" stroke="none" stroke-width="1" fill-rule="evenodd">
                  <g id="Icon-24px" transform="translate(1.000000, 6.000000)">
                      <path d="M0.718118899,4.12779989 C6.39726421,-1.3759333 15.6027358,-1.3759333 21.2818811,4.12779989 C22.1977429,5.01537339 22.237563,6.43435594 21.4013413,7.36813892 L21.2818811,7.49237006 L19.4124482,9.30406169 C18.4984787,10.1898014 17.0384269,10.2300623 16.0748135,9.42484437 L15.9406442,9.30406169 L15.7097621,9.09016092 C15.2395977,8.67392072 14.7188856,8.32691761 14.1449865,8.04679724 C13.3849238,7.66249118 12.8796751,6.92460021 12.8293886,6.1129924 L12.8256496,5.93805011 L12.8236986,4.92785585 L12.7526019,4.91379893 C11.7278494,4.73245394 10.6764706,4.71322365 9.64364868,4.85587652 L9.25729854,4.91695917 L9.18531475,4.93193216 L9.18475643,5.96425715 C9.17027171,6.73631033 8.77812057,7.43656001 8.1357651,7.87837694 L7.98314574,7.97566218 L7.83299366,8.0577341 C7.27796869,8.32848002 6.75995294,8.67414392 6.29017374,9.09019281 L5.95452868,9.39993074 C5.03728896,10.1912309 3.6506817,10.1999265 2.72312621,9.42601756 L2.58755183,9.30406169 L0.718118899,7.49237006 C-0.239372966,6.56445231 -0.239372966,5.05571764 0.718118899,4.12779989 Z M19.8900218,5.56401981 C14.9865413,0.811993396 7.01345875,0.811993396 2.1099782,5.56401981 C1.98428883,5.68582701 1.9663332,5.85598083 2.05611133,5.99160135 L2.1099782,6.05615013 L3.97941113,7.86784176 C4.13195512,8.01567411 4.38807229,8.04040391 4.54445861,7.96328676 L4.60478263,7.92406299 L4.66749653,7.86784176 C5.26063943,7.2930193 5.92456413,6.80717133 6.58911166,6.4485679 L6.96741533,6.25278908 C7.08366763,6.18425182 7.15175599,6.09579061 7.17534824,6.01853231 L7.18475672,5.96317547 L7.18619439,3.30495121 L8.90638203,2.94798543 C10.1548638,2.7254772 11.4325909,2.70216173 12.7134794,2.88368008 L13.1405235,2.95178038 L14.8205274,3.28394333 L14.8252823,5.97637387 C14.8235625,6.06611172 14.8998395,6.18734817 15.0222628,6.24946968 C15.6529921,6.5573289 16.2365263,6.93027198 16.7929478,7.38737179 L17.0689922,7.62302305 L17.3325035,7.86784176 C17.4916022,8.02202637 17.7598822,8.04129945 17.9462762,7.92566099 L18.0205889,7.86784176 L19.8900218,6.05615013 C20.0366594,5.91404173 20.0366594,5.70612821 19.8900218,5.56401981 Z" id="Shape"></path>
                  </g>
              </g>
          </svg>`
          text = `<div class='message-icon'><span class="svg-icon-container icon-type-ended-call">${svgIcon}</span>${translation.ENDED_CALL}</div>`;
          this.translate.get("ENDED_CALL")
          .pipe(take(1))
            .subscribe(res => {
              text = `<div class='message-icon'><span class="svg-icon-container icon-type-ended-call">${svgIcon}</span> ${res} </div>`;
            });
        }

    return text;
  }
    saveFluidSettings(data: any): Observable<any> {
      return this.post(VNCTALK_API + "/fluidsettings", true, data);
    }

    getFluidSettings (): Observable<any>  {
      let url = VNCTALK_API + "/fluidsettings"
      return this.get<string>(url, false);
    }

    sendInviteReply(body: any): Observable<any> {
      return this.post(VNCMAIL_API + "/sendInviteReply", true, body);
    }

    updateContact(contact: ContactRest): Observable<null> {
      const data = this.mapModelToMidlewareParams(contact);
      return this.put(VNCTALK_API + `/contacts/${contact.id}`, true, data);
    }

    private mapModelToMidlewareParams(contact: any): any {
      let contactParams = {...contact};
      if (contact.phones && contact.phones.length > 0) {
        let phones_attributes = {};
        for (let i = 0; i < contact.phones.length; ++i) {
          phones_attributes[i.toString()] = contact.phones[i];
        }
        contactParams["phones_attributes"] = phones_attributes;
      }
      if (contact.urls && contact.urls.length > 0) {
        let urls_attributes = {};
        for (let i = 0; i < contact.urls.length; ++i) {
          urls_attributes[i.toString()] = contact.urls[i];
        }
        contactParams["urls_attributes"] = urls_attributes;
      }
      if (contact.emails && contact.emails.length > 0) {
        let emails_attributes = {};
        for (let i = 0; i < contact.emails.length; ++i) {
          emails_attributes[i.toString()] = contact.emails[i];
        }
        contactParams["emails_attributes"] = emails_attributes;
        this.store.select(getUserProfile).pipe(take(1)).subscribe(v => {
          const ownDomain = v.email.split("@")[1];
          const contactDomain = contact.jid.split("@")[1];
          contactParams["is_iom"] = false;
          contactParams["jid"] = contact.jid;
          if (contactDomain !== ownDomain) {
            contactParams["is_iom"] = true;
          }
        });
      }
      if (contact.addresses && contact.addresses.length > 0) {
        let addresses_attributes = {};
        for (let i = 0; i < contact.addresses.length; ++i) {
          addresses_attributes[i.toString()] = contact.addresses[i];
        }
        contactParams["addresses_attributes"] = addresses_attributes;
      }
      return {"contact": contactParams};
    }

    searchLdap(body: any): Observable<any> {
      return this.post(VNCTALK_API + "/search-ldap", true, body);
    }

    updateTicketData(data, issue_id): Observable<any> {
      // let dataToUpdate = {
      //   "issue": {
      //       "subject": "Subject changed",
      //     }
      //   }
      return this.put(VNCTASK_API + `/task/issues/${issue_id}.json`, true, data);
    }

  download(fileUrl: string) {
    return this.http.get(fileUrl, {
      responseType: "blob",
      observe: "response",
    });
  }
}
